// src/pages/task/TaskWorkspacePage.tsx
// Unified task workspace page. Replaces separate TaskListPage, TaskBoardPage,
// TaskCalendarPage, TaskTimelinePage, TaskSchedulePage with a single workspace
// that switches views while preserving shared state (filters, sort, search).

import { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import {
  useTask,
  useTaskStatusDefinition,
  useTeam,
  useProject,
  useI18n,
} from '@react/hooks';
import { Alert, Button, ErrorAlert } from '@react/components';
import { ListMyTasks as apiListMyTasks } from '@core/api/endpoints/task/list_my_tasks';
import { ListTasks as apiListTasks } from '@core/api/endpoints/task/list_tasks';
import { ListTasksByParent as apiListTasksByParent } from '@core/api/endpoints/task/list_tasks_by_parent';
import { ListScheduledTasks as apiListScheduledTasks } from '@core/api/endpoints/task/list_scheduled_tasks';
import { GetAssigneeAvailability as apiGetAssigneeAvailability } from '@core/api/endpoints/task/get_assignee_availability';

import { ScopeProvider, useScope } from '@/components/ScopeProvider';
import { useFullPaneSurface } from '@/features/tabs/PaneSurface';
import { ScopeOptions, useScopeLabel } from '@/components/ScopeOptions';
import { Select } from '@/components/Select';
import { SettingsSection } from '@/components/workspace/SettingsSurface';
import { useScope as usePlatformScope } from '@/hooks/useScope';
import { WorkspaceLayout, useWorkspace } from '@/layouts/WorkspaceLayout';
import {
  TASK_WORKSPACE_CONFIG,
  buildTaskViewFilter,
  buildTaskSort,
  hasActiveFilters,
} from '@/features/workspace/logic';
import { TaskSavedViews, TaskSavedViewsMenu } from '@/features/workspace/TaskSavedViews';
import { usePageActions } from '@/hooks/usePageActions';
import { ACTION_ICONS } from '@/features/layout/actionIcons';
import { datedFilename, downloadCsv } from '@/features/export/csv';
import { SplitView } from '@/components/SplitView';
import { TaskSplitDetail } from '@/components/TaskSplitDetail';
import { BulkActionsBar } from '@/components/BulkActionsBar';
import {
  TaskQuickCreateDrawer,
  onTaskQuickCreateRequested,
} from '@/features/task/form';
import {
  applyTaskViewFilter,
  buildStatusChangeRequest,
  buildTaskFieldUpdate,
  bulkUpdateStatus,
  bulkUpdatePriority,
  bulkAssign,
  bulkDelete,
  getDisplayName,
  getEntityId,
  getTaskAssignee,
  getTaskDueDate,
  getTaskPriority,
  getTaskStatus,
  type BulkResult,
} from '@/features/task/logic';

import { ListView } from './views/ListView';
import { BoardView } from './views/BoardView';
import { CalendarView } from './views/CalendarView';
import { TimelineView, type TimelineDateChange } from './views/TimelineView';
import { ScheduleView, type ScheduleMoveTarget } from './views/ScheduleView';
import {
  UNASSIGNED_KEY,
  addDays,
  collectActors,
  daysBetween,
  endOfDay,
  getActorKey,
  getScheduledWork,
  moveIsoToDay,
} from './views/planning';

import type { PaginatedRequest } from '@core/types/pagination';
import type { Actor } from '@core/types/actor';
import type { Entity } from '@core/types/entity';
import type {
  ActorSchedule,
  ListTasksResponse,
  Task,
  TaskStatus,
  TaskViewFilter,
} from '@core/types/task';
import type { ScopeContext } from '@/features/scope/types';
import type { DensityMode } from '@/features/workspace/types';

/**
 * The generated list endpoints type their query object as `PaginatedRequest`,
 * but the gateway reads a ListMyTasksRequest / ListTasksByParentRequest:
 * a nested PaginatedRequest plus a structured `TaskViewFilter`. The API client
 * serializes this by json path (`pagination.pagination.cursor=…`,
 * `filters.statuses.statuses=…`), which the DTO's `query` tags bind.
 * Model the real request here and cast once at the call boundary.
 */
type TaskListParams = {
  pagination: PaginatedRequest;
  filters?: TaskViewFilter;
  assigned_to_me?: boolean;
  created_by_me?: boolean;
  watching?: boolean;
};

type PersonalLens = 'all' | 'assigned' | 'created' | 'watching';
const PERSONAL_LENSES: PersonalLens[] = ['all', 'assigned', 'created', 'watching'];
type PersonalFocus = 'all' | 'overdue' | 'today' | 'upcoming' | 'waiting';
const PERSONAL_FOCUSES: PersonalFocus[] = ['all', 'overdue', 'today', 'upcoming', 'waiting'];

const ACTIVE_TASK_STATUSES: TaskStatus[] = [
  'TASK_STATUS_OPEN',
  'TASK_STATUS_IN_PROGRESS',
  'TASK_STATUS_IN_REVIEW',
  'TASK_STATUS_BLOCKED',
];

const asPaginatedRequest = (params: TaskListParams): PaginatedRequest =>
  params as unknown as PaginatedRequest;

/** How many tasks one page of the workspace holds. */
const PAGE_SIZE = 50;

/** Views that paginate through a flat list; the rest render their own grid. */
const PAGINATED_VIEWS = new Set(['list', 'board']);

/** One week of work blocks is small; ask for a page big enough to hold it. */
const SCHEDULE_PAGE_SIZE = 200;

/**
 * Merge a patch into a list row. List responses carry task fields either flat
 * or nested under `task` (see the accessors in features/task/logic.ts), so a
 * patch has to land in both places or the optimistic value stays invisible.
 */
function applyRowPatch(row: Task, patch: Record<string, unknown>): Task {
  const next = { ...row, ...patch } as unknown as Record<string, unknown>;
  const nested = (row as unknown as Record<string, unknown>).task;
  if (nested && typeof nested === 'object') {
    next.task = { ...(nested as Record<string, unknown>), ...patch };
  }
  return next as unknown as Task;
}

type ToastTone = 'info' | 'success' | 'warning' | 'error';

interface ToastState {
  tone: ToastTone;
  message: string;
}

// ---------------------------------------------------------------------------
// Inner workspace (needs scope + workspace context)
// ---------------------------------------------------------------------------

interface TaskWorkspaceInnerProps {
  /** Whether the quick-create drawer is on screen. Owned by the page, because
      the workspace's own Create button lives in the layout above this. */
  createOpen: boolean;
  onCreateOpenChange: (open: boolean) => void;
}

const TaskWorkspaceInner = observer(({ createOpen, onCreateOpenChange }: TaskWorkspaceInnerProps) => {
  const store = useTask();
  const statusDefStore = useTaskStatusDefinition();
  const { t } = useI18n();
  const navigate = useNavigate();
  const { scope } = useScope();
  const platformScope = usePlatformScope();
  const { state, dispatch } = useWorkspace();

  // Load custom status definitions on mount
  useEffect(() => {
    statusDefStore.ListStatusDefinitions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Split view state
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  // Deep link into a task's split preview: /tasks?view=board&task=<id>
  const [searchParams, setSearchParams] = useSearchParams();
  const personalLens = PERSONAL_LENSES.find((lens) => lens === searchParams.get('mine')) ?? 'all';
  const personalFocus = PERSONAL_FOCUSES.find((focus) => focus === searchParams.get('focus')) ?? 'all';
  const setPersonalLens = useCallback((value: string) => {
    if (!PERSONAL_LENSES.includes(value as PersonalLens)) return;
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (value === 'all') next.delete('mine');
      else next.set('mine', value);
      return next;
    });
  }, [setSearchParams]);
  const setPersonalFocus = useCallback((value: PersonalFocus) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (value === 'all') next.delete('focus');
      else next.set('focus', value);
      return next;
    });
  }, [setSearchParams]);
  useEffect(() => {
    const tid = searchParams.get('task');
    if (tid) setSelectedTaskId(tid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bulk selection state
  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkLoading, setIsBulkLoading] = useState(false);

  // Transient feedback for actions that do not have their own surface
  const [toast, setToast] = useState<ToastState | null>(null);
  const notify = useCallback((tone: ToastTone, message: string) => {
    setToast({ tone, message });
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 6000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  // ── Request shape ────────────────────────────────────────────────────
  // `filters` is the structured TaskViewFilter the task service expects;
  // `sort_by` / `sort_desc` carry the sort. Both are derived from URL state,
  // so a shared link reproduces the exact same request.
  const viewFilter: TaskViewFilter | undefined = useMemo(
    () => buildTaskViewFilter(state),
    [state],
  );

  // Focus is a server filter. Applying it to the already loaded page would
  // miss older overdue work hidden behind the next cursor.
  const focusedViewFilter = useMemo(() => {
    if (personalFocus === 'all' || scope.scopeType !== 'personal') return viewFilter;
    const filter = { ...viewFilter } as TaskViewFilter;
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    if (personalFocus === 'overdue') {
      filter.due_before = new Date(start.getTime() - 1).toISOString();
    } else if (personalFocus === 'today') {
      filter.due_after = start.toISOString();
      filter.due_before = new Date(end.getTime() - 1).toISOString();
    } else if (personalFocus === 'upcoming') {
      filter.due_after = end.toISOString();
    }
    const focusStatuses: TaskStatus[] = personalFocus === 'waiting'
      ? ['TASK_STATUS_IN_REVIEW', 'TASK_STATUS_BLOCKED']
      : [...ACTIVE_TASK_STATUSES];
    const selectedStatuses = filter.statuses?.statuses;
    const statuses = selectedStatuses?.length
      ? focusStatuses.filter((status) => selectedStatuses.includes(status))
      : focusStatuses;
    filter.statuses = { statuses: statuses.length ? statuses : ['TASK_STATUS_UNSPECIFIED'] };
    return filter;
  }, [personalFocus, scope.scopeType, viewFilter]);

  const sortParams = useMemo(() => buildTaskSort(state), [state]);

  // Which list endpoint the current scope maps to. Without this the workspace
  // is pinned to "my tasks" and the scope switcher does nothing.
  const isScopedToEntity =
    !!scope.scopeEntity?.id &&
    (scope.scopeType === 'team' || scope.scopeType === 'project' || scope.scopeType === 'entity');
  const isTenantWide = !isScopedToEntity && scope.scopeType === 'all';

  const buildParams = useCallback(
    (cursor: string): TaskListParams => ({
      pagination: {
        pagination: { cursor, limit: PAGE_SIZE, direction: 'forward' },
        // All three list RPCs nest their pagination settings under the
        // request's `pagination` field. Keep sort in that DTO too.
        ...(sortParams.sort ? {
          sort: [{
            field: sortParams.sort.split(':')[0],
            order: sortParams.sort.split(':')[1],
          }],
        } : {}),
        view: 2,
      },
      // STANDARD view: rows carry `parent`, which the list's parent grouping
      // and subtask indentation read. BASIC strips it.
      filters: focusedViewFilter,
      ...(!isScopedToEntity && !isTenantWide && personalLens !== 'all' ? {
        assigned_to_me: personalLens === 'assigned',
        created_by_me: personalLens === 'created',
        watching: personalLens === 'watching',
      } : {}),
    }),
    [focusedViewFilter, sortParams, isScopedToEntity, isTenantWide, personalLens],
  );

  // ── Rows: server page(s) plus local optimistic edits ─────────────────
  const [rows, setRows] = useState<Task[]>([]);
  const [nextCursor, setNextCursor] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const fetchFirstPage = useCallback(() => {
    const params = asPaginatedRequest(buildParams(''));
    if (isScopedToEntity) {
      return store.ListTasksByParent(
        scope.scopeEntity?.type || scope.scopeType,
        scope.scopeEntity?.id ?? '',
        params,
      );
    }
    if (isTenantWide) {
      return store.ListTasks(params);
    }
    return store.ListMyTasks(params);
  }, [
    store,
    buildParams,
    isScopedToEntity,
    isTenantWide,
    scope.scopeEntity?.type,
    scope.scopeEntity?.id,
    scope.scopeType,
  ]);

  // Load the first page whenever the request shape changes
  useEffect(() => {
    fetchFirstPage().catch(() => { /* surfaced through store.error */ });
  }, [fetchFirstPage, platformScope.productId]);

  // Mirror the store page into local rows so inline edits can be optimistic
  // without writing through the observable list.
  useEffect(() => {
    setRows([...(store.items as Task[])]);
    setNextCursor(store.pageInfo?.next_cursor ?? '');
    setHasMore(!!store.pageInfo?.has_next);
  }, [store.items, store.pageInfo]);

  const handleLoadMore = useCallback(async () => {
    if (!hasMore || isLoadingMore || !nextCursor) return;
    setIsLoadingMore(true);
    try {
      const params = asPaginatedRequest(buildParams(nextCursor));
      let response: ListTasksResponse;
      if (isScopedToEntity) {
        response = await apiListTasksByParent(
          scope.scopeEntity?.type || scope.scopeType,
          scope.scopeEntity?.id ?? '',
          params,
        );
      } else if (isTenantWide) {
        response = await apiListTasks(params);
      } else {
        response = await apiListMyTasks(params);
      }

      const page = (response.tasks?.tasks ?? []) as Task[];
      setRows((prev) => {
        const seen = new Set(prev.map((task) => getEntityId(task)));
        return [...prev, ...page.filter((task) => !seen.has(getEntityId(task)))];
      });
      setNextCursor(response.pagination?.page_info?.next_cursor ?? '');
      setHasMore(!!response.pagination?.page_info?.has_next);
    } catch {
      notify('error', t('workspace.loadMoreError'));
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    hasMore,
    isLoadingMore,
    nextCursor,
    buildParams,
    isScopedToEntity,
    isTenantWide,
    scope.scopeEntity?.type,
    scope.scopeEntity?.id,
    scope.scopeType,
    notify,
    t,
  ]);

  // The server applies `filters` in SQL. Re-checking here only matters for
  // rows this page holds that the server never filtered — a task created by
  // quick-add, or one an inline edit just moved out of the active filter.
  const tasks: Task[] = useMemo(
    () => applyTaskViewFilter(rows, viewFilter),
    [rows, viewFilter],
  );

  const statusDefinitions = statusDefStore.items;

  // ── Schedule view data ───────────────────────────────────────────────
  // The schedule view plans real work blocks, which live outside the paged
  // task list: ListScheduledTasks for the visible week and
  // GetAssigneeAvailability for the capacity behind each person-day.
  const [scheduleRange, setScheduleRange] = useState<{ start: Date; end: Date } | null>(null);
  const [scheduledRows, setScheduledRows] = useState<Task[]>([]);
  const [isScheduleLoading, setIsScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState<unknown>(null);
  const [availability, setAvailability] = useState<Record<string, ActorSchedule>>({});
  const [isAvailabilityLoading, setIsAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<unknown>(null);

  // ── Optimistic row patching ──────────────────────────────────────────
  const patchRow = useCallback((taskId: string, patch: Record<string, unknown>) => {
    setRows((prev) =>
      prev.map((row) => (getEntityId(row) === taskId ? applyRowPatch(row, patch) : row)),
    );
    setScheduledRows((prev) =>
      prev.map((row) => (getEntityId(row) === taskId ? applyRowPatch(row, patch) : row)),
    );
  }, []);

  /** A task may be known from the paged list, the scheduled result, or both. */
  const findTask = useCallback(
    (taskId: string): Task | undefined =>
      rows.find((task) => getEntityId(task) === taskId) ??
      scheduledRows.find((task) => getEntityId(task) === taskId),
    [rows, scheduledRows],
  );

  /**
   * Apply `patch` locally, run `commit`, and roll the row back if the server
   * rejects it. Nothing is refetched on success: the row already shows the
   * value the server accepted. Returns whether the commit succeeded so
   * callers can chain follow-up writes (assign → reschedule) or report.
   */
  const optimistic = useCallback(
    async (
      taskId: string,
      patch: Record<string, unknown>,
      commit: () => Promise<unknown>,
      errorKey: string,
    ): Promise<boolean> => {
      const previous = findTask(taskId);
      if (!previous) return false;
      const flat = previous as unknown as Record<string, unknown>;
      const nested = (flat.task ?? {}) as Record<string, unknown>;
      const rollback = Object.keys(patch).reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = nested[key] ?? flat[key];
        return acc;
      }, {});

      patchRow(taskId, patch);
      try {
        await commit();
        return true;
      } catch {
        patchRow(taskId, rollback);
        notify('error', t(errorKey));
        return false;
      }
    },
    [findTask, patchRow, notify, t],
  );

  // ── Inline editing ───────────────────────────────────────────────────
  const handleStatusChange = useCallback(
    (taskId: string, columnId: string) => {
      const request = buildStatusChangeRequest(taskId, columnId, statusDefinitions);
      return optimistic(
        taskId,
        { status: request.status, custom_status: request.custom_status || '' },
        () => store.ChangeTaskStatus(taskId, request),
        'task.statusChangeFailed',
      );
    },
    [optimistic, statusDefinitions, store],
  );

  const handlePriorityChange = useCallback(
    (taskId: string, priority: string) =>
      optimistic(
        taskId,
        { priority },
        () => store.UpdateTask(taskId, buildTaskFieldUpdate(taskId, { priority: priority as Task['priority'] })),
        'task.updateFailed',
      ),
    [optimistic, store],
  );

  const handleDueDateChange = useCallback(
    (taskId: string, date: string) => {
      const iso = date ? new Date(date).toISOString() : '';
      return optimistic(
        taskId,
        { due_date: iso },
        () => store.UpdateTask(taskId, buildTaskFieldUpdate(taskId, { due_date: iso })),
        'task.updateFailed',
      );
    },
    [optimistic, store],
  );

  const handleTitleChange = useCallback(
    (taskId: string, title: string) =>
      optimistic(
        taskId,
        { title },
        () => store.UpdateTask(taskId, buildTaskFieldUpdate(taskId, { title })),
        'task.updateFailed',
      ),
    [optimistic, store],
  );

  const handleTaskClick = useCallback((id: string) => setSelectedTaskId(id), []);
  const handleCloseSplit = useCallback(() => setSelectedTaskId(null), []);

  // ── Board: transitions, drag & drop, quick add ───────────────────────
  const [allowedColumnIds, setAllowedColumnIds] = useState<string[] | null>(null);
  const transitionsRequestId = useRef(0);

  const handleDragStartTask = useCallback(
    async (taskId: string) => {
      const requestId = ++transitionsRequestId.current;
      setAllowedColumnIds(null);
      try {
        const response = await store.GetAvailableTransitions(taskId);
        if (requestId !== transitionsRequestId.current) return;
        const allowed = (response?.transitions ?? []).flatMap((transition) =>
          [transition.to_status, transition.category].filter(Boolean) as string[],
        );
        // An empty transition list means "unconstrained" rather than "nothing
        // allowed": not every deployment configures a workflow.
        setAllowedColumnIds(allowed.length > 0 ? allowed : null);
      } catch {
        setAllowedColumnIds(null);
      }
    },
    [store],
  );

  const handleTaskMove = useCallback(
    (taskId: string, columnId: string) => {
      transitionsRequestId.current++;
      setAllowedColumnIds(null);
      return handleStatusChange(taskId, columnId);
    },
    [handleStatusChange],
  );

  const handleBlockedMove = useCallback(() => {
    notify('warning', t('task.transitionNotAllowed'));
  }, [notify, t]);

  const handleQuickAdd = useCallback(
    async (columnId: string, title: string) => {
      const definition = statusDefinitions.find((def) => def.slug === columnId);
      const parent = scope.scopeEntity?.id
        ? { id: scope.scopeEntity.id, type_name: scope.scopeEntity.type }
        : undefined;

      try {
        const created = await store.CreateTask({
          title,
          type: 'TASK_TYPE_TASK',
          parent: parent as never,
        });
        // CreateTask has no status field, so a non-default column needs a
        // follow-up transition on the freshly created task.
        const createdId = created ? getEntityId(created) : '';
        const targetStatus = definition ? definition.slug : columnId;
        if (createdId && targetStatus !== 'TASK_STATUS_OPEN') {
          await store
            .ChangeTaskStatus(createdId, buildStatusChangeRequest(createdId, targetStatus, statusDefinitions))
            .catch(() => notify('warning', t('task.quickAddStatusFailed')));
        }
        await fetchFirstPage();
        notify('success', t('task.quickAddSuccess'));
      } catch {
        notify('error', t('task.quickAddFailed'));
      }
    },
    [statusDefinitions, scope.scopeEntity?.id, scope.scopeEntity?.type, store, fetchFirstPage, notify, t],
  );

  // ── Quick create ─────────────────────────────────────────────────────
  // ⌘N asks whoever is showing a task list to handle it; while this workspace
  // is mounted, that is us. Elsewhere the shortcut still opens the full page.
  const openQuickCreate = useCallback(() => onCreateOpenChange(true), [onCreateOpenChange]);
  useEffect(() => onTaskQuickCreateRequested(openQuickCreate), [openQuickCreate]);

  /** What the drawer reads the parent off: the scope this list is showing. */
  const quickCreateScope = useMemo(
    () => ({ scopeType: scope.scopeType, scopeEntity: scope.scopeEntity }),
    [scope.scopeType, scope.scopeEntity],
  );

  /**
   * A task was just filed from the drawer. The list updates in place — the
   * point of creating here was not leaving it. The new row goes in
   * immediately so the answer is visible before the network settles, then the
   * first page is refetched so the server's ordering, grouping and filtering
   * decide where it really belongs (and drop it from view if the active filter
   * excludes it).
   */
  const handleQuickCreated = useCallback(
    (created: Task | null, createdId: string) => {
      if (created && createdId) {
        setRows((prev) =>
          prev.some((row) => getEntityId(row) === createdId) ? prev : [created, ...prev],
        );
      }
      notify('success', t('task.quickAddSuccess'));
      void fetchFirstPage().catch(() => { /* surfaced through store.error */ });
    },
    [fetchFirstPage, notify, t],
  );

  // ── Planning views: scheduled work + availability ────────────────────
  const scopeEntity = useMemo<Entity | undefined>(
    () =>
      scope.scopeEntity?.id
        ? ({ id: scope.scopeEntity.id, type_name: scope.scopeEntity.type } as Entity)
        : undefined,
    [scope.scopeEntity?.id, scope.scopeEntity?.type],
  );

  /** The schedule view reports the week it is showing; that drives the fetch. */
  const handleScheduleRange = useCallback((range: { start: Date; end: Date }) => {
    setScheduleRange((prev) =>
      prev &&
      prev.start.getTime() === range.start.getTime() &&
      prev.end.getTime() === range.end.getTime()
        ? prev
        : range,
    );
  }, []);

  /**
   * `ListScheduledTasks` is a GET whose `scope` / `assignees` / `pagination`
   * are nested objects the generated query serializer flattens, so only the
   * top-level `time_min` / `time_max` reliably reach the server today
   * (framework-giph). The request is built in its real shape regardless; when
   * the transport is fixed nothing here has to change. A failure degrades to
   * the due-date derived rows rather than blanking the view.
   */
  const fetchScheduled = useCallback(async () => {
    if (!scheduleRange) return;
    setIsScheduleLoading(true);
    setScheduleError(null);
    try {
      const response = await apiListScheduledTasks({
        pagination: { cursor: '', limit: SCHEDULE_PAGE_SIZE, direction: 'forward' },
        time_min: scheduleRange.start.toISOString(),
        time_max: scheduleRange.end.toISOString(),
      });
      setScheduledRows((response.tasks?.tasks ?? []) as Task[]);
    } catch (error) {
      setScheduledRows([]);
      setScheduleError(error);
    } finally {
      setIsScheduleLoading(false);
    }
  }, [scheduleRange]);

  useEffect(() => {
    if (state.view !== 'schedule' || !scheduleRange) return;
    void fetchScheduled();
  }, [state.view, scheduleRange, fetchScheduled]);

  // Every assignee visible in the week — from the loaded page as well as the
  // scheduled result, so someone with no due-dated task still gets capacity.
  const scheduleActors = useMemo<Actor[]>(
    () => (state.view === 'schedule' ? collectActors([...tasks, ...scheduledRows]) : []),
    [state.view, tasks, scheduledRows],
  );

  const actorSignature = useMemo(
    () => scheduleActors.map(getActorKey).sort().join(','),
    [scheduleActors],
  );

  useEffect(() => {
    if (state.view !== 'schedule' || !scheduleRange || scheduleActors.length === 0) {
      return;
    }
    let cancelled = false;
    setIsAvailabilityLoading(true);
    setAvailabilityError(null);
    apiGetAssigneeAvailability({
      scope: scopeEntity,
      actors: scheduleActors,
      time_min: scheduleRange.start.toISOString(),
      time_max: scheduleRange.end.toISOString(),
      respect_working_hours: true,
    })
      .then((response) => {
        if (cancelled) return;
        // The map is keyed by whatever id the service used; index it by the
        // same actor key the rows use as well, so a URN-keyed response still
        // lines up with the grid.
        const normalized: Record<string, ActorSchedule> = {};
        for (const [key, schedule] of Object.entries(response.actor_schedules ?? {})) {
          normalized[key] = schedule;
          const actorKey = getActorKey(schedule?.actor);
          if (actorKey !== UNASSIGNED_KEY) normalized[actorKey] = schedule;
        }
        setAvailability(normalized);
      })
      .catch((error) => {
        if (cancelled) return;
        setAvailability({});
        setAvailabilityError(error);
      })
      .finally(() => {
        if (!cancelled) setIsAvailabilityLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `actorSignature` stands in for `scheduleActors`: the actor objects are
    // rebuilt on every page change, their identities are not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.view, scheduleRange, actorSignature, scopeEntity]);

  /**
   * Move a task onto `date`. A scheduled task keeps its duration and its time
   * of day and is rescheduled; an unscheduled one just gets a new due date.
   */
  const moveTaskToDay = useCallback(
    async (taskId: string, date: Date): Promise<boolean> => {
      const task = findTask(taskId);
      if (!task) return false;

      const work = getScheduledWork(task);
      const start = work?.start_time ? new Date(work.start_time) : null;

      if (work && start && !Number.isNaN(start.getTime())) {
        const delta = daysBetween(start, date);
        if (delta === 0) return true;

        const end = work.end_time ? new Date(work.end_time) : null;
        const newStart = addDays(start, delta);
        const newEnd = end && !Number.isNaN(end.getTime()) ? addDays(end, delta) : newStart;

        return optimistic(
          taskId,
          {
            scheduled_work: {
              ...work,
              start_time: newStart.toISOString(),
              end_time: newEnd.toISOString(),
            },
          },
          () =>
            store.RescheduleTask(taskId, {
              task: { id: taskId } as Entity,
              new_start_time: newStart.toISOString(),
              new_end_time: newEnd.toISOString(),
              timezone: work.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
              notify_assignee: true,
            }),
          'task.updateFailed',
        );
      }

      const iso = moveIsoToDay(getTaskDueDate(task), date);
      return optimistic(
        taskId,
        { due_date: iso },
        () => store.UpdateTask(taskId, buildTaskFieldUpdate(taskId, { due_date: iso })),
        'task.updateFailed',
      );
    },
    [findTask, optimistic, store],
  );

  const handleCalendarDrop = useCallback(
    async (taskId: string, date: Date) => {
      if (await moveTaskToDay(taskId, date)) {
        notify('success', t('task.planning.moved'));
      }
    },
    [moveTaskToDay, notify, t],
  );

  const handleQuickCreateOnDay = useCallback(
    async (date: Date, title: string) => {
      const parent = scope.scopeEntity?.id
        ? { id: scope.scopeEntity.id, type_name: scope.scopeEntity.type }
        : undefined;
      try {
        await store.CreateTask({
          title,
          type: 'TASK_TYPE_TASK',
          due_date: endOfDay(date).toISOString(),
          parent: parent as never,
        });
        await fetchFirstPage();
        notify('success', t('task.quickAddSuccess'));
      } catch {
        notify('error', t('task.quickAddFailed'));
      }
    },
    [scope.scopeEntity?.id, scope.scopeEntity?.type, store, fetchFirstPage, notify, t],
  );

  const handleTimelineDates = useCallback(
    async (taskId: string, dates: TimelineDateChange) => {
      const patch: Record<string, unknown> = { ...dates };
      if (Object.keys(patch).length === 0) return;
      const ok = await optimistic(
        taskId,
        patch,
        () => store.UpdateTask(taskId, buildTaskFieldUpdate(taskId, dates)),
        'task.updateFailed',
      );
      if (ok) notify('success', t('task.planning.datesUpdated'));
    },
    [optimistic, store, notify, t],
  );

  const handleScheduleMove = useCallback(
    async (taskId: string, target: ScheduleMoveTarget) => {
      const task = findTask(taskId);
      if (!task) return;

      if (target.actorKey !== getActorKey(getTaskAssignee(task))) {
        const assigned = await optimistic(
          taskId,
          { assignee: target.actor ?? undefined },
          () =>
            store.AssignTask(taskId, {
              task: { id: taskId } as Entity,
              assignee: target.actor ?? undefined,
            }),
          'task.updateFailed',
        );
        if (!assigned) return;
      }

      if (await moveTaskToDay(taskId, target.date)) {
        notify('success', t('task.planning.moved'));
      }
    },
    [findTask, moveTaskToDay, optimistic, store, notify, t],
  );

  // ── Bulk actions ─────────────────────────────────────────────────────
  const reportBulkResult = useCallback(
    (result: BulkResult) => {
      if (result.failed === 0) {
        notify('success', t('workspace.bulk.allSucceeded', { count: result.success }));
      } else if (result.success === 0) {
        notify('error', t('workspace.bulk.allFailed', { count: result.failed }));
      } else {
        notify(
          'warning',
          t('workspace.bulk.partialFailure', { success: result.success, failed: result.failed }),
        );
      }
    },
    [notify, t],
  );

  const runBulk = useCallback(
    async (action: (ids: string[]) => Promise<BulkResult>) => {
      const ids = Array.from(bulkSelectedIds);
      if (ids.length === 0) return;
      setIsBulkLoading(true);
      try {
        const result = await action(ids);
        reportBulkResult(result);
        setBulkSelectedIds(new Set());
        await fetchFirstPage();
      } finally {
        setIsBulkLoading(false);
      }
    },
    [bulkSelectedIds, reportBulkResult, fetchFirstPage],
  );

  const handleBulkStatusChange = useCallback(
    (status: string) => runBulk((ids) => bulkUpdateStatus(ids, status, statusDefinitions)),
    [runBulk, statusDefinitions],
  );

  const handleBulkPriorityChange = useCallback(
    (priority: string) => runBulk((ids) => bulkUpdatePriority(ids, priority)),
    [runBulk],
  );

  const handleBulkAssign = useCallback(
    (assigneeId: string) => runBulk((ids) => bulkAssign(ids, assigneeId)),
    [runBulk],
  );

  const handleBulkDelete = useCallback(() => runBulk((ids) => bulkDelete(ids)), [runBulk]);

  const handleClearSelection = useCallback(() => setBulkSelectedIds(new Set()), []);

  // ── Split navigation ─────────────────────────────────────────────────
  const taskIds = useMemo(() => tasks.map((task) => getEntityId(task)), [tasks]);

  const handleSplitNavigate = useCallback(
    (direction: 'prev' | 'next') => {
      if (!selectedTaskId) return;
      const idx = taskIds.indexOf(selectedTaskId);
      if (idx === -1) return;
      const nextIdx = direction === 'prev'
        ? (idx - 1 + taskIds.length) % taskIds.length
        : (idx + 1) % taskIds.length;
      setSelectedTaskId(taskIds[nextIdx]);
    },
    [selectedTaskId, taskIds],
  );

  // ── Page actions (Right Rail) ────────────────────────────────────────
  // Rare, list-wide operations live in the rail; the everyday Create button
  // stays in the page header where the eye already looks for it.
  const handleExportCsv = useCallback(() => {
    downloadCsv(datedFilename('tasks'), [
      [
        t('workspace.export.columns.title'),
        t('workspace.export.columns.status'),
        t('workspace.export.columns.priority'),
        t('workspace.export.columns.assignee'),
        t('workspace.export.columns.dueDate'),
      ],
      ...tasks.map((task) => [
        task.title ?? '',
        getTaskStatus(task),
        getTaskPriority(task),
        getDisplayName(getTaskAssignee(task)),
        getTaskDueDate(task) ?? '',
      ]),
    ]);
  }, [tasks, t]);

  const nextDensity: DensityMode =
    state.density === 'compact' ? 'normal' : state.density === 'normal' ? 'relaxed' : 'compact';

  usePageActions([
    {
      key: 'export-csv',
      icon: ACTION_ICONS.export,
      label: t('workspace.actions.exportCsv', { count: tasks.length }),
      onSelect: handleExportCsv,
    },
    {
      key: 'density',
      icon: ACTION_ICONS.density,
      label: t('workspace.actions.density', { mode: t(`workspace.density.${nextDensity}`) }),
      onSelect: () => dispatch({ type: 'SET_DENSITY', density: nextDensity }),
    },
    // Bulk editing starts with a selection, and selecting a long list by hand
    // is the part that hurts.
    ...(bulkSelectedIds.size > 0
      ? [{
        key: 'clear-selection',
        icon: ACTION_ICONS.clearSelection,
        label: t('workspace.actions.clearSelection', { count: bulkSelectedIds.size }),
        onSelect: handleClearSelection,
      }]
      : [{
        key: 'select-all',
        icon: ACTION_ICONS.selectAll,
        label: t('workspace.actions.selectAll', { count: taskIds.length }),
        onSelect: () => setBulkSelectedIds(new Set(taskIds)),
      }]),
    ...(hasActiveFilters(state)
      ? [{
        key: 'clear-filters',
        icon: ACTION_ICONS.filter,
        label: t('workspace.clearFilters'),
        onSelect: () => dispatch({ type: 'CLEAR_FILTERS' }),
      }]
      : []),
    {
      key: 'refresh',
      icon: ACTION_ICONS.refresh,
      label: t('task.refresh'),
      onSelect: () => { void fetchFirstPage(); },
    },
    // Custom statuses are configured once per scope; the action is a door to
    // that settings page, next to the other list-wide operations.
    {
      key: 'customize-statuses',
      icon: ACTION_ICONS.tune,
      label: t('task.customizeStatuses'),
      onSelect: () => navigate('/tasks/settings'),
    },
  ]);

  const splitDetail = selectedTaskId ? (
    <TaskSplitDetail
      taskId={selectedTaskId}
      onClose={handleCloseSplit}
      onNavigate={handleSplitNavigate}
      onUpdated={(taskId, patch) => patchRow(taskId, patch)}
    />
  ) : null;

  const filtersActive = hasActiveFilters(state);
  const isInitialLoad = store.isLoading && rows.length === 0;
  const isEmpty = !store.isLoading && tasks.length === 0;

  const renderView = () => {
    switch (state.view) {
      case 'board':
        return (
          <BoardView
            tasks={tasks}
            statusDefinitions={statusDefinitions}
            groupBy={state.groupBy}
            density={state.density}
            onTaskClick={handleTaskClick}
            onTaskMove={handleTaskMove}
            onDragStartTask={handleDragStartTask}
            allowedColumnIds={allowedColumnIds}
            onBlockedMove={handleBlockedMove}
            onQuickAdd={handleQuickAdd}
          />
        );
      case 'calendar':
        return (
          <CalendarView
            tasks={tasks}
            statusDefinitions={statusDefinitions}
            onTaskClick={handleTaskClick}
            onTaskDrop={handleCalendarDrop}
            onQuickCreate={handleQuickCreateOnDay}
          />
        );
      case 'timeline':
        return (
          <TimelineView
            tasks={tasks}
            statusDefinitions={statusDefinitions}
            onTaskClick={handleTaskClick}
            onTaskDatesChange={handleTimelineDates}
          />
        );
      case 'schedule':
        return (
          <ScheduleView
            tasks={tasks}
            scheduledTasks={scheduledRows}
            availability={availability}
            statusDefinitions={statusDefinitions}
            isScheduleLoading={isScheduleLoading}
            isAvailabilityLoading={isAvailabilityLoading}
            scheduleError={scheduleError}
            availabilityError={availabilityError}
            onRetrySchedule={() => void fetchScheduled()}
            onRangeChange={handleScheduleRange}
            onTaskClick={handleTaskClick}
            onMove={handleScheduleMove}
          />
        );
      case 'list':
      default:
        return (
          <ListView
            tasks={tasks}
            density={state.density}
            groupBy={state.groupBy}
            statusDefinitions={statusDefinitions}
            onTaskClick={handleTaskClick}
            selectedIds={bulkSelectedIds}
            onSelectionChange={setBulkSelectedIds}
            onStatusChange={handleStatusChange}
            onPriorityChange={handlePriorityChange}
            onDueDateChange={handleDueDateChange}
            onTitleChange={handleTitleChange}
          />
        );
    }
  };

  /**
   * The list, in whatever state it is in. Written as a function rather than
   * early returns because the drawer must outlive every one of them: creating
   * the first task from an empty list would otherwise unmount the panel that
   * just created it.
   */
  const body = () => {
    // ── Error state ────────────────────────────────────────────────────
    if (store.error && !store.isLoading && rows.length === 0) {
      return (
        <div className="space-y-3">
          <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />
          <Button size="sm" variant="secondary" onClick={() => fetchFirstPage()}>
            {t('common.retry')}
          </Button>
        </div>
      );
    }

    // ── Loading state ──────────────────────────────────────────────────
    if (isInitialLoad) {
      return <WorkspaceLoadingSkeleton view={state.view} />;
    }

    // ── Empty state ────────────────────────────────────────────────────
    // Calendar / timeline / schedule keep their own chrome so the grid, date
    // navigation and legends stay usable while there is nothing to plot.
    if (isEmpty && PAGINATED_VIEWS.has(state.view)) {
      return (
        <>
          <WorkspaceToast toast={toast} onDismiss={() => setToast(null)} />
          <EmptyState
            filtersActive={filtersActive}
            focusActive={!isScopedToEntity && !isTenantWide && personalFocus !== 'all'}
            onClearFocus={() => setPersonalFocus('all')}
            personalLens={!isScopedToEntity && !isTenantWide ? personalLens : 'all'}
            onCreate={openQuickCreate}
          />
        </>
      );
    }

    return (
      <>
        <WorkspaceToast toast={toast} onDismiss={() => setToast(null)} />

        {/* `showClose={false}`: TaskSplitDetail draws its own header, with the
            close beside prev / next and the link out to the full page. */}
        <SplitView detail={splitDetail} onClose={handleCloseSplit} showClose={false}>
          <div className="space-y-3">
            {renderView()}

            <ResultSummary
              shown={tasks.length}
              hasMore={hasMore}
              canLoadMore={PAGINATED_VIEWS.has(state.view)}
              isLoadingMore={isLoadingMore}
              onLoadMore={handleLoadMore}
            />
          </div>
        </SplitView>

        <BulkActionsBar
          selectedCount={bulkSelectedIds.size}
          onStatusChange={handleBulkStatusChange}
          onPriorityChange={handleBulkPriorityChange}
          onAssign={handleBulkAssign}
          onDelete={handleBulkDelete}
          onClearSelection={handleClearSelection}
          isLoading={isBulkLoading}
        />
      </>
    );
  };

  return (
    <>
      {!isScopedToEntity && !isTenantWide && (
        <div className="mb-4 flex flex-wrap items-end gap-3 border-b border-border pb-4">
          <Select
            fullWidth={false}
            className="min-w-[12rem] flex-1 sm:flex-none sm:w-56 [&>button]:h-10"
            label={t('task.workspace.personalLens')}
            value={personalLens}
            options={PERSONAL_LENSES.map((lens) => ({
              value: lens,
              label: t(lens === 'all' ? 'task.workspace.allMine' : `myWork.tab.${lens}`),
            }))}
            onChange={(event) => setPersonalLens(event.target.value as PersonalLens)}
          />
          <Select
            fullWidth={false}
            className="min-w-[12rem] flex-1 sm:flex-none sm:w-56 [&>button]:h-10"
            label={t('task.workspace.focus')}
            value={personalFocus}
            options={PERSONAL_FOCUSES.map((focus) => ({
              value: focus,
              label: t(`task.workspace.focusOptions.${focus}`),
            }))}
            onChange={(event) => setPersonalFocus(event.target.value as PersonalFocus)}
          />
        </div>
      )}
      {body()}

      {createOpen && (
        <TaskQuickCreateDrawer
          scope={quickCreateScope}
          onClose={() => onCreateOpenChange(false)}
          onCreated={handleQuickCreated}
        />
      )}
    </>
  );
});

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------

function WorkspaceToast({ toast, onDismiss }: { toast: ToastState | null; onDismiss: () => void }) {
  if (!toast) return null;
  return (
    <div className="fixed bottom-20 left-1/2 z-50 w-[min(28rem,90vw)] -translate-x-1/2" role="status" aria-live="polite">
      <Alert variant={toast.tone} dismissible onDismiss={onDismiss}>
        {toast.message}
      </Alert>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Result summary + load more
// ---------------------------------------------------------------------------

interface ResultSummaryProps {
  shown: number;
  hasMore: boolean;
  canLoadMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
}

function ResultSummary({ shown, hasMore, canLoadMore, isLoadingMore, onLoadMore }: ResultSummaryProps) {
  const { t } = useI18n();
  if (shown === 0) return null;

  return (
    <div className="flex flex-col items-center gap-2 py-2">
      <p className="text-xs text-muted-foreground">
        {hasMore
          ? t('workspace.showingCountOfMore', { count: shown })
          : t('workspace.showingCount', { count: shown })}
      </p>
      {hasMore && canLoadMore && (
        <Button size="sm" variant="secondary" onClick={onLoadMore} disabled={isLoadingMore}>
          {isLoadingMore ? t('common.loading') : t('workspace.loadMore')}
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

interface EmptyStateProps {
  filtersActive: boolean;
  focusActive: boolean;
  onClearFocus: () => void;
  personalLens: PersonalLens;
  /** Opens the quick-create drawer — creating from here keeps you here. */
  onCreate: () => void;
}

function EmptyState({ filtersActive, focusActive, onClearFocus, personalLens, onCreate }: EmptyStateProps) {
  const { t } = useI18n();
  const { dispatch } = useWorkspace();

  return (
    <div className="py-16 text-center">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
        <svg className="h-8 w-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      </div>

      <p className="text-lg font-medium text-foreground">
        {filtersActive || focusActive ? t('workspace.noMatches') : personalLens === 'all' ? t('task.noItems') : t(`myWork.empty.${personalLens}`)}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        {filtersActive || focusActive ? t('workspace.noMatchesDescription') : personalLens === 'all' ? t('task.noItemsDescription') : t('myWork.subtitle')}
      </p>

      <div className="mt-5 flex items-center justify-center gap-2">
        {filtersActive ? (
          <Button size="sm" variant="secondary" onClick={() => dispatch({ type: 'CLEAR_FILTERS' })}>
            {t('workspace.clearFilters')}
          </Button>
        ) : null}
        {focusActive && (
          <Button size="sm" variant="secondary" onClick={onClearFocus}>
            {t('myWork.focusFilter.clear')}
          </Button>
        )}
        <Button size="sm" onClick={onCreate}>
          {t('task.create')}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function WorkspaceLoadingSkeleton({ view }: { view: string }) {
  if (view === 'board') {
    return (
      <div className="flex gap-4 overflow-x-auto pb-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex-shrink-0 w-72">
            <div className="flex items-center gap-2 mb-3 px-1">
              <div className="h-3 w-3 rounded-full bg-muted animate-pulse" />
              <div className="h-4 w-20 bg-muted rounded animate-pulse" />
            </div>
            <div className="space-y-3 min-h-[200px] rounded-lg bg-muted/30 p-2">
              {Array.from({ length: 3 }).map((_, j) => (
                <div key={j} className="rounded-lg border border-border bg-card p-3 animate-pulse">
                  <div className="h-4 w-full bg-muted rounded mb-2" />
                  <div className="h-3 w-2/3 bg-muted rounded mb-3" />
                  <div className="flex items-center justify-between">
                    <div className="h-5 w-16 bg-muted rounded-full" />
                    <div className="h-5 w-5 bg-muted rounded-full" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Default list skeleton
  return (
    <div className="space-y-1">
      {Array.from({ length: 10 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2.5 animate-pulse">
          <div className="h-4 w-4 rounded bg-muted" />
          <div className="h-4 w-4 rounded bg-muted" />
          <div className="h-4 w-16 rounded bg-muted" />
          <div className="h-4 flex-1 rounded bg-muted" />
          <div className="h-5 w-16 rounded-full bg-muted" />
          <div className="h-3 w-3 rounded-full bg-muted" />
          <div className="h-6 w-6 rounded-full bg-muted" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scope section fed with the caller's teams and projects
// ---------------------------------------------------------------------------

const TaskScopeSection = observer(() => {
  const { t } = useI18n();
  const teamStore = useTeam();
  const projectStore = useProject();
  const scopeLabel = useScopeLabel();

  useEffect(() => {
    teamStore.ListTeams({ pagination: { cursor: '', limit: 50, direction: 'forward' } });
    projectStore.ListProjects({ pagination: { cursor: '', limit: 50, direction: 'forward' } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const teams = useMemo(
    () =>
      (teamStore.items ?? []).map((team: any) => ({
        id: team.entity?.id ?? '',
        type: team.entity?.type_name ?? 'team',
        name: team.name ?? team.entity?.id ?? '',
      })).filter((team) => team.id),
    [teamStore.items],
  );

  const projects = useMemo(
    () =>
      (projectStore.items ?? []).map((project: any) => ({
        id: project.entity?.id ?? '',
        type: project.entity?.type_name ?? 'project',
        name: project.name ?? project.entity?.id ?? '',
      })).filter((project) => project.id),
    [projectStore.items],
  );

  return (
    <SettingsSection id="scope" label={t('scope.switcherLabel')} summary={scopeLabel}>
      <ScopeOptions teams={teams} projects={projects} />
    </SettingsSection>
  );
});

// ---------------------------------------------------------------------------
// Exported page component (wraps in providers)
// ---------------------------------------------------------------------------

/**
 * `/tasks` opens on the caller's own work, as it always has. The switcher can
 * widen it to the whole tenant or narrow it to a team / project.
 */
const DEFAULT_TASK_SCOPE: ScopeContext = {
  scopeType: 'personal',
  scopeEntity: null,
  scopeLabel: 'scope.personal',
};

export const TaskWorkspacePage = observer(() => {
  // Creating opens the drawer beside the list instead of navigating away: the
  // list is what tells you whether the task already exists and what it belongs
  // to. The full page is still one quiet link away, inside the drawer.
  const [createOpen, setCreateOpen] = useState(false);
  const handleCreate = useCallback(() => setCreateOpen(true), []);

  // The split view's list and preview scroll inside the pane, and the divider
  // between them is only as tall as the surface it can measure. Claiming the
  // pane gives the split a bounded height to fill; without it the panels hang
  // off the content and the divider stops wherever the content happened to
  // end. The padding the pane used to supply moves here so the page looks
  // unchanged.
  const paneSurface = useFullPaneSurface();

  return (
    <ScopeProvider defaultScope={DEFAULT_TASK_SCOPE}>
      {/* While the drawer is open the workspace gives up the width it covers,
          so the list stays whole and readable rather than sitting underneath
          it. Below `sm` there is no room to share and the panel takes over. */}
      <div
        className={`${paneSurface} flex flex-col px-6 py-4 transition-[padding] duration-200 motion-reduce:transition-none ${
          createOpen ? 'sm:pr-[25rem]' : ''
        }`}
      >
        <WorkspaceLayout
          config={TASK_WORKSPACE_CONFIG}
          title="task.workspace.title"
          createLabel="task.create"
          onCreateClick={handleCreate}
          scope={<TaskScopeSection />}
          savedViews={<TaskSavedViews />}
          savedViewsMenu={<TaskSavedViewsMenu />}
        >
          <TaskWorkspaceInner createOpen={createOpen} onCreateOpenChange={setCreateOpen} />
        </WorkspaceLayout>
      </div>
    </ScopeProvider>
  );
});

export default TaskWorkspacePage;
